import { PromotionCart } from '../promotion.types';

export interface ProductUnit {
  cartItemId: string;
  productId: string;
  basePrice: number;
  stableKey: string;
}

export interface ProductAggregate {
  productId: string;
  units: ProductUnit[];
  totalUnits: number;
  totalSubtotal: number;
}

/**
 * Aggregates a `PromotionCart` into per-product unit lists, sorted by base price ASC.
 * The cart engine uses this as the single input for family math: best-of-family,
 * cheapest-first assignment and position-as-quantity-gate all operate on this aggregate.
 *
 * The unit of evaluation is the **product aggregate across the whole cart**, not the
 * cart line. Same product split into multiple `PromotionCartItem`s (multi-presentation
 * in single mode, notes fragmentation, etc.) is collapsed here for promo math; the
 * original cartItemIds are preserved on each `ProductUnit` so discounts can be rolled
 * back into `discountByItemId`.
 *
 * - One `ProductUnit` per actual purchasable unit (PromotionCartItem expanded by `quantity`).
 * - `basePrice` = `subtotal / quantity` of the source `PromotionCartItem`. The subtotal
 *   already has the individual `priceDiscount` baked in by `expandCartItemsToPromotionCart`.
 * - Sort order: `basePrice` ASC, tiebreak by `stableKey` ASC for deterministic output
 *   when units share a price.
 * - Pure: same input always gives the same aggregate (within each product, units order is stable).
 */
export function aggregateCartByProduct(cart: PromotionCart): Map<string, ProductAggregate> {
  const unitsByProduct = new Map<string, ProductUnit[]>();

  for (const item of cart.items) {
    if (item.quantity <= 0) {
      continue;
    }
    const basePrice = item.subtotal / item.quantity;
    let units = unitsByProduct.get(item.productId);
    if (!units) {
      units = [];
      unitsByProduct.set(item.productId, units);
    }
    for (let unitIndex = 0; unitIndex < item.quantity; unitIndex++) {
      units.push({
        cartItemId: item.id,
        productId: item.productId,
        basePrice,
        stableKey: `${item.id}#${unitIndex}`,
      });
    }
  }

  const result = new Map<string, ProductAggregate>();
  for (const [productId, units] of unitsByProduct) {
    units.sort((a, b) => {
      if (a.basePrice !== b.basePrice) {
        return a.basePrice - b.basePrice;
      }
      if (a.stableKey < b.stableKey) {
        return -1;
      }
      if (a.stableKey > b.stableKey) {
        return 1;
      }
      return 0;
    });
    const totalSubtotal = units.reduce((sum, unit) => sum + unit.basePrice, 0);
    result.set(productId, {
      productId,
      units,
      totalUnits: units.length,
      totalSubtotal,
    });
  }

  return result;
}
